import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  FolderTree, 
  HelpCircle, 
  Users, 
  Gamepad2, 
  TrendingUp,
  BarChart3,
  Layers,
  Sparkles
} from 'lucide-react';
import { apiClient } from '@/lib/api/apiClient';
import { endpoints } from '@/lib/api/apiEndpoints';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { sectionService } from '@/features/admin/services/sectionService';

interface DashboardStats {
  totalSections: number;
  totalCategories: number;
  totalQuestions: number;
  totalUsers: number;
  activeGames: number;
  pendingQuestions: number;
  todayGames: number;
  activeSections: number;
  activeCategories: number;
}

export const AdminDashboard = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      // Fetch all stats in parallel
      const [sectionsRes, categoriesRes, questionsRes, usersRes] = await Promise.all([
        sectionService.getAllSections({ pageSize: 1000 }).catch(() => ({ sections: [], totalCount: 0 })),
        apiClient.get(endpoints.admin.categories(), { params: { pageSize: 1000 } }).catch(() => ({ data: { data: { items: [] } } })),
        apiClient.get(endpoints.admin.questions(), { params: { pageSize: 1 } }).catch(() => ({ data: { data: { totalCount: 0 } } })),
        apiClient.get(endpoints.admin.users(), { params: { pageSize: 1 } }).catch(() => ({ data: { data: { totalCount: 0 } } })),
      ]);

      const sections = sectionsRes.sections || [];
      const categories = categoriesRes.data?.data?.items || [];
      const questionsTotal = questionsRes.data?.data?.totalCount || 0;
      const usersTotal = usersRes.data?.data?.totalCount || 0;
      
      setStats({
        totalSections: sections.length,
        activeSections: sections.filter((s: any) => s.isEnabled).length,
        totalCategories: categories.length,
        activeCategories: categories.filter((c: any) => c.isActive || c.isEnabled).length,
        totalQuestions: questionsTotal,
        totalUsers: usersTotal,
        activeGames: 0, // TODO: Fetch from games API
        pendingQuestions: 0, // TODO: Fetch from questions API with filter
        todayGames: 0, // TODO: Fetch from games API
      });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  const statCards = [
    {
      title: 'الأقسام',
      value: stats?.totalSections || 0,
      subValue: `${stats?.activeSections || 0} نشط`,
      icon: Layers,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
      link: '/admin/sections',
    },
    {
      title: 'الفئات',
      value: stats?.totalCategories || 0,
      subValue: `${stats?.activeCategories || 0} نشط`,
      icon: FolderTree,
      color: 'text-secondary',
      bgColor: 'bg-secondary/10',
      link: '/admin/categories',
    },
    {
      title: 'الأسئلة',
      value: stats?.totalQuestions || 0,
      icon: HelpCircle,
      color: 'text-accent-red',
      bgColor: 'bg-accent-red/10',
      link: '/admin/questions',
    },
    {
      title: 'المستخدمين',
      value: stats?.totalUsers || 0,
      icon: Users,
      color: 'text-accent-greenDark',
      bgColor: 'bg-accent-greenDark/10',
      link: '/admin/users',
    },
    {
      title: 'ألعاب نشطة',
      value: stats?.activeGames || 0,
      icon: Gamepad2,
      color: 'text-accent-yellowGreen',
      bgColor: 'bg-accent-yellowGreen/10',
      link: '/admin/games',
    },
    {
      title: 'ألعاب اليوم',
      value: stats?.todayGames || 0,
      icon: TrendingUp,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
      link: '/admin/games',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-secondary/10 to-accent-greenLight/10 rounded-xl p-6 border-2 border-accent-greenLight/30">
        <h1 className="text-3xl font-bold text-accent-dark mb-2">مرحباً بك في لوحة التحكم</h1>
        <p className="text-accent-dark/70">إدارة المحتوى واللاعبين والألعاب من مكان واحد</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Link key={index} to={stat.link}>
              <Card className="hover:shadow-lg transition-all duration-300 hover:scale-105 cursor-pointer border-2 border-accent-greenLight/20">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-lg font-semibold text-accent-dark">
                    {stat.title}
                  </CardTitle>
                  <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                    <Icon className={`w-6 h-6 ${stat.color}`} />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-4xl font-bold text-accent-dark mb-1">
                    {stat.value.toLocaleString()}
                  </div>
                  {stat.subValue ? (
                    <p className="text-sm text-accent-dark/60">
                      <span className={`font-medium ${stat.color}`}>{stat.subValue}</span>
                    </p>
                  ) : (
                    <p className="text-sm text-accent-dark/60">انقر للعرض</p>
                  )}
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      {/* Quick Actions */}
      <Card className="border-2 border-accent-greenLight/20">
        <CardHeader>
          <CardTitle className="text-xl font-bold text-accent-dark">إجراءات سريعة</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <Link
              to="/admin/sections/create"
              className="p-4 bg-purple-100 hover:bg-purple-200 rounded-lg border-2 border-purple-300 transition-all duration-200 text-center"
            >
              <Layers className="w-8 h-8 mx-auto mb-2 text-purple-600" />
              <p className="font-medium text-accent-dark">إضافة قسم</p>
            </Link>
            <Link
              to="/admin/categories/create"
              className="p-4 bg-secondary/10 hover:bg-secondary/20 rounded-lg border-2 border-secondary/30 transition-all duration-200 text-center"
            >
              <FolderTree className="w-8 h-8 mx-auto mb-2 text-secondary" />
              <p className="font-medium text-accent-dark">إضافة فئة</p>
            </Link>
            <Link
              to="/admin/questions/create"
              className="p-4 bg-accent-red/10 hover:bg-accent-red/20 rounded-lg border-2 border-accent-red/30 transition-all duration-200 text-center"
            >
              <HelpCircle className="w-8 h-8 mx-auto mb-2 text-accent-red" />
              <p className="font-medium text-accent-dark">إضافة سؤال</p>
            </Link>
            <Link
              to="/admin/questions/import"
              className="p-4 bg-accent-greenDark/10 hover:bg-accent-greenDark/20 rounded-lg border-2 border-accent-greenDark/30 transition-all duration-200 text-center"
            >
              <Sparkles className="w-8 h-8 mx-auto mb-2 text-accent-greenDark" />
              <p className="font-medium text-accent-dark">استيراد Excel</p>
            </Link>
            <Link
              to="/admin/reports"
              className="p-4 bg-accent-yellowGreen/10 hover:bg-accent-yellowGreen/20 rounded-lg border-2 border-accent-yellowGreen/30 transition-all duration-200 text-center"
            >
              <BarChart3 className="w-8 h-8 mx-auto mb-2 text-accent-yellowGreen" />
              <p className="font-medium text-accent-dark">التقارير</p>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
